<template>
  <v-container fluid>
    <v-row justify="center" dense>
      <v-btn absolute left icon large color="primary" class="mr-3" @click="fetchAll">
        <v-icon>mdi-reload</v-icon>
      </v-btn>

      <Dialog
        width="600px"
        :disabled="!isUploadable"
        @apply="uploadPosture"
        outlined
      >
        <v-icon class="mr-2">mdi-upload</v-icon>
        アップロード

        <template #title>
          姿勢撮影データ選択
        </template>

        <template #contents>
          <v-container 
            dense
            fluid 
            @dragleave.prevent 
            @dragover.prevent 
            @drop.prevent="selectFiles"
          >
            <v-row>
              <v-col>
                <v-select
                  label="計測参加者"
                  :items="participants"
                  item-text="name"
                  item-value="id"
                  v-model="participantId"
                />
              </v-col>
            </v-row>
            <v-row 
              dense
              align="center"
            >
              <v-col>
                <v-file-input
                  accept="image/*,video/mp4"
                  dense
                  hide-details
                  :label="`${maxFileSize}MB以下`"
                  outlined
                  multiple
                  v-model="selectedFiles"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  dense
                  v-model="postureName"
                  label="登録名"
                />
              </v-col>
            </v-row>
          </v-container>

          <v-alert 
            v-if="!participantId"
            type="error"
            dense
          >
            計測参加者が選択されていません
          </v-alert>

          <v-alert 
            v-if="selectedFiles.length == 0"
            type="error"
            dense
          >
            撮影データが選択されていません
          </v-alert>

          <v-alert 
            v-if="!postureName"
            type="error"
            dense
          >
            登録名が未記入です
          </v-alert>

          <v-alert 
            v-if="isFileSizeExceeded"
            type="error"
            dense
          >
            ファイルサイズが上限を超えています
          </v-alert>
        </template>

        <template #apply>アップロード</template>
      </Dialog>
    </v-row>
    <v-row dense>
      <v-col>
        <PosturesTable
          :loading="isLoading"
          :postures="postures"
          height="calc(100vh - 196px)"
          :items-per-page.sync="itemsPerPage"
          :page.sync="currentPage"
          :filter-params="filterParams"
          class="mt-3"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";

import PosturesTable from "@components/parts/tables/PosturesTable.vue";
import Dialog from "@components/parts/dialogs/Dialog.vue";

export default {
  components: {
    PosturesTable,
    Dialog,
  },
  data: function(){
    return {
      itemsPerPage: -1,
      currentPage: 1,
      isLoading: false,
      isUploading: false,
      postureName: null,
      selectedFiles: [],
      participantId: null,

      filterParams: {
        isDeleted: false,
      },

      maxFileSize: 100, //MB
    }
  },
  computed: {
    ...mapState([
      'user',
      'project',
      'participants',
      'postures',
    ]),
    isVideo: function(){
      if(this.selectedFiles.length == 0){
        return false;
      }
      return this.selectedFiles[0].type === "video/mp4";
    },
    isFileSizeExceeded: function(){
      if(this.selectedFiles.length == 0){
        return false;
      }
      if(this.maxFileSize*1000*1000 < this.selectedFiles.reduce((prev,current) => prev + current.size, 0)) {
        return true;
      }
      return false;
    },
    isUploadable: function(){
      if(!this.postureName || this.selectedFiles.length == 0 || !this.participantId){
        return false;
      }

      if(this.isFileSizeExceeded){
        return false;
      }

      return true;
    },
  },
  watch: {
    project: {
      handler: function(newVal){
        if(newVal){
          this.fetchAll();
        }
      },
    },
    selectedFiles: function(newVal){
      if(newVal.length == 0){
        return;
      }

      if(!this.postureName){
        this.postureName = newVal[0].name.split('.').slice(0, -1).join('.');
      }
    },
  },
  destroyed: function(){
  },
  mounted: function(){
    if(!this.project){
      this.$router.push('/');
      return;
    }

    this.fetchAll();
  },
  methods: {
    ...mapActions([
      'fetchParticipants',
      'fetchPostures',
    ]),
    fetchAll: function(){
      this.fetchPostures();
      this.fetchParticipants();
    },
    uploadPosture: function(){
      if(this.selectedFiles.length == 0 || !this.participantId){
        return;
      }
      
      if(this.isFileSizeExceeded) {
        this.selectedFiles = [];
        alert(`${this.maxFileSize}MB以下のファイルのみ対応しています`)
        return;
      }

      const params = new FormData();
      this.selectedFiles.forEach(f => {
        params.append('files', f);
      });
      params.append('posture_data_form', 
        JSON.stringify({
          name: this.postureName,
          projectId: this.project.id,
          participantId: this.participantId
        })
      );
      this.isUploading = true;

      this.axios.post(
        `/api/posture/${this.isVideo ? 'video' : 'image'}`,
        params,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      ).then(() => {
        this.fetchAll();
      }).catch(error => {
        console.log(error);
      }).finally(() => {
        this.isUploading = false;
      });
    },
    selectFiles: function(e){
      var files = Array.from(e.dataTransfer.files);
      files = files.filter(f => f.size <= this.maxFileSize*1000*1000 && (f.type.startsWith('image/') || f.type === 'video/mp4'));
      if(files.length == 0){
        return;
      }

      this.selectedFiles = files[0].type === "video/mp4" ? [files[0]] : files.filter(f => f.type != 'video/mp4');
      this.postureName = this.selectedFiles[0].name.split('.').slice(0, -1).join('.');
    }
  }
}
</script>

<style lang="scss" scoped>

</style>